import { DeviceUnknown, Edit, HistoryOutlined } from '@mui/icons-material';
import { Box, Button, Grid, IconButton, Stack, Tooltip } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { format, parse } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import StatusChip from '@sophocles/components/StatusChip';

// One label/value row. Full width (default, `compact` false) keeps the
// original 4/8 side-by-side split with a bare value node; `compact` stacks
// label above value and shrinks both for narrow hosts (the account rail),
// wrapping the value in its own Typography so its size can shrink too.
function AgreementField({ label, value, compact }) {
  return (
    <Grid size={12}>
      <Grid container direction={compact ? 'column' : 'row'}>
        <Grid size={compact ? 12 : 4}>
          <Typography
            fontWeight="bold"
            variant={compact ? 'caption' : 'body1'}
            color={compact ? 'text.secondary' : undefined}
          >
            {label}
          </Typography>
        </Grid>
        <Grid size={compact ? 12 : 8}>{compact ? <Typography variant="body2">{value}</Typography> : value}</Grid>
      </Grid>
    </Grid>
  );
}

function AgreementCard({ agreement, onEdit, onShowHistory, historyCount = 0, sx = {}, compact = false }) {
  const { t } = useTranslation();

  const frequencyMap = {
    none: t('billing.none', 'None'),
    month: t('billing.monthly', 'Monthly'),
    quarter: t('billing.quarterly', 'Quarterly'),
    semiannual: t('billing.semiAnnually', 'Semi-Annually'),
    annual: t('billing.annually', 'Annually'),
    prepaid: t('billing.prepaid', 'Prepaid'),
    trial: t('billing.trial', 'Trial'),
    demo: t('billing.demo', 'Demo'),
    internal: t('billing.internal', 'Internal'),
  };

  if (agreement == null) {
    if (compact) {
      return (
        <Box sx={{ textAlign: 'center', py: 1.5 }}>
          <DeviceUnknown fontSize="medium" />
          <Typography variant="body2">{t('billing.noAgreementFound', 'No Agreement Found')}</Typography>
        </Box>
      );
    }
    return (
      <Card>
        <CardContent>
          <DeviceUnknown fontSize="large" />
          <Typography>{t('billing.noAgreementFound', 'No Agreement Found')}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Compact hosts (the account rail) render frameless — the agreement reads
  // as account detail, not a card within a card — with the edit affordance at
  // the header's trailing edge.
  const header = compact ? (
    <Stack
      sx={{ width: '100%', mb: 1 }}
      direction="row"
      spacing={1}
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
    >
      <Typography variant="subtitle1" component="div">
        {t('billing.agreement', 'Agreement')}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <StatusChip
          color={agreement.is_signed ? 'ok' : 'bad'}
          label={agreement.is_signed ? t('billing.signed', 'Signed') : t('billing.unsigned', 'Unsigned')}
        />
        <StatusChip
          color={agreement.is_active ? 'ok' : 'bad'}
          label={agreement.is_active ? t('billing.active', 'Active') : t('billing.inactive', 'Inactive')}
        />
        {onEdit != null && (
          <Button size="small" variant="text" onClick={() => onEdit(agreement?.id)}>
            {t('general.edit', 'Edit')}
          </Button>
        )}
        {onShowHistory != null && (
          <Tooltip title={`${t('billing.account.olderAgreements', 'Older Agreements')} (${historyCount})`}>
            <IconButton size="small" onClick={onShowHistory}>
              <HistoryOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  ) : (
    <Stack
      sx={{ width: '100%', mb: 2 }}
      direction="row"
      spacing={2}
      justifyContent="space-between"
      alignItems="center"
      flexWrap="nowrap"
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h5" component="div">
          {t('billing.agreement', 'Agreement')}
        </Typography>
        {onEdit != null && (
          <IconButton size="small" onClick={() => onEdit(agreement?.id)}>
            <Edit fontSize="medium" />
          </IconButton>
        )}
      </Stack>
      <Stack direction="row" spacing={2} flexWrap="nowrap">
        <StatusChip
          color={agreement.is_signed ? 'ok' : 'bad'}
          label={agreement.is_signed ? t('billing.signed', 'Signed') : t('billing.unsigned', 'Unsigned')}
        />
        <StatusChip
          color={agreement.is_active ? 'ok' : 'bad'}
          label={agreement.is_active ? t('billing.active', 'Active') : t('billing.inactive', 'Inactive')}
        />
      </Stack>
    </Stack>
  );

  const body = (
    <>
      {header}
      <Grid container spacing={0}>
        <AgreementField
          compact={compact}
          label={t('billing.duration', 'Duration')}
          value={`${agreement.start_date ?? t('billing.na', 'N/A')} - ${agreement.end_date ?? t('billing.na', 'N/A')}`}
        />
        <AgreementField
          compact={compact}
          label={t('billing.pricingGuarantee', 'Pricing Guarantee')}
          value={`${agreement.pricing_guarantee_start_date ?? t('billing.na', 'N/A')} - ${agreement.pricing_guarantee_end_date ?? t('billing.na', 'N/A')}`}
        />
        <AgreementField
          compact={compact}
          label={t('billing.invoiceFrequency', 'Invoice Frequency')}
          value={frequencyMap[agreement.billing_frequency] ?? t('billing.na', 'N/A')}
        />
        {agreement.billing_frequency === 'annual' && (
          <AgreementField
            compact={compact}
            label={t('billing.annualBillingMonth', 'Annual Billing Month')}
            value={
              agreement.annual_start_date
                ? format(parse(agreement.annual_start_date, 'yyyy-MM-dd', new Date()), 'MMMM')
                : t('billing.na', 'N/A')
            }
          />
        )}
        <AgreementField
          compact={compact}
          label={t('billing.salesPerson', 'Sales Person')}
          value={`${agreement.primary_sales_person.first_name} ${agreement.primary_sales_person.last_name}`}
        />
        <AgreementField
          compact={compact}
          label={t('billing.billSiteGroupsSeparately', 'Bill Site Groups Separately')}
          value={agreement.is_billed_by_site_group ? t('billing.yes', 'Yes') : t('billing.no', 'No')}
        />
        <AgreementField
          compact={compact}
          label={t('billing.billVirtualAccess', 'Bill Virtual Access')}
          value={agreement.is_billed_virtual ? t('billing.yes', 'Yes') : t('billing.no', 'No')}
        />
      </Grid>
      <AgreementField
        compact={compact}
        label={t('billing.billInventory', 'Bill Inventory')}
        value={agreement.inventory_policy}
      />
      <AgreementField
        compact={compact}
        label={t('billing.dataAccessFee', 'Data Access Fee')}
        value={`$${(agreement.device_price ?? 0).toFixed(2)}`}
      />
      {agreement.is_billed_virtual && (
        <AgreementField
          compact={compact}
          label={t('billing.virtualAccessFee', 'Virtual Access Fee')}
          value={`$${(agreement.virtual_price ?? 0).toFixed(2)}`}
        />
      )}
      {agreement.inventory_policy !== 'none' && (
        <AgreementField
          compact={compact}
          label={t('billing.inventoryFee', 'Inventory Fee')}
          value={`$${(agreement.inventory_price ?? 0).toFixed(2)}`}
        />
      )}
    </>
  );

  if (compact) {
    return <Box sx={sx}>{body}</Box>;
  }

  return (
    <Card sx={sx}>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

export default AgreementCard;
